import { Dialog, Theme, useMediaQuery } from "@mui/material";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import toast from "react-hot-toast";
import { useEffect, useState } from "react";
import { truncateAddress } from "@/utils/truncateText";
import { getNetworkDisplayName } from "@/utils/utils";

interface CompleteRefundModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (txId: string) => void;
  destination: {
    network: string;
    walletAddress: string;
    bankName: string;
    bankAccountNumber: string;
    bankAccountName: string;
  };
  txId?: string;
}

const CompleteRefundModal = ({
  open,
  onClose,
  onSubmit,
  destination,
  txId,
}: CompleteRefundModalProps) => {
  const [, copy] = useCopyToClipboard();
  const [transactionId, setTransactionId] = useState<string>(txId || "");
  const [error, setError] = useState<string>("");
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm"),
  );
  const handleSubmit = () => {
    if (!transactionId.trim()) {
      setError("TXID is required");
      return;
    }
    onSubmit(transactionId.trim());
  };

  const handleCopy = (text: string) => () => {
    copy(text)
      .then(() => {
        toast.success("Copied!");
      })
      .catch(() => {
        toast.error("Failed to copy!");
      });
  };

  useEffect(() => {
    if (txId) {
      setTransactionId(txId);
    }
    setTransactionId("");
  }, [txId]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableScrollLock
      PaperProps={{
        sx: {
          borderRadius: "24px",
          width: `${isMobile ? "100%" : "670px"}`,
          margin: "16px",
        },
      }}
    >
      <div className="rounded-[24px] px-8 py-6 max-sm:px-3">
        <div className="flex items-center justify-between border-b border-[#E2E8F0] pb-4">
          <h2 className="text-xl font-medium text-[#1E293B]">
            {txId ? "Refund Completed" : "Complete Refund"}
          </h2>
          <button onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="#334155"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <div className="mt-8">
          <div className="rounded-[8px] border border-[#E2E8F0] bg-[#F6F9FB] p-4">
            <h3 className="mb-2 font-semibold text-text">Destination</h3>
            <div className="space-y-2">
              {destination?.walletAddress ? (
                <>
                  <div className="flex">
                    <span className="w-[150px] text-[#64748B] max-sm:text-sm">
                      Network
                    </span>
                    <span className="text-sm font-semibold text-text">
                      {destination?.network
                        ? getNetworkDisplayName(destination.network)
                        : "-"}
                    </span>
                  </div>
                  <div className="flex">
                    <span className="w-[150px] text-[#64748B] max-sm:text-sm">
                      Wallet Address
                    </span>
                    <div className="flex items-center gap-2">
                      {isMobile ? (
                        <>
                          {destination.walletAddress ? (
                            <>
                              <span className="text-sm font-semibold text-text">
                                {truncateAddress(
                                  destination.walletAddress,
                                  4,
                                  5,
                                )}
                              </span>
                              <button
                                onClick={handleCopy(destination.walletAddress)}
                              >
                                <img
                                  src="/images/copy.png"
                                  alt="copy"
                                  className="size-4"
                                />
                              </button>
                            </>
                          ) : (
                            "-"
                          )}
                        </>
                      ) : (
                        <>
                          {destination.walletAddress ? (
                            <>
                              <span className="text-sm font-semibold text-text">
                                {destination.walletAddress.length > 44
                                  ? truncateAddress(
                                      destination.walletAddress,
                                      15,
                                      20,
                                    )
                                  : destination.walletAddress}
                              </span>
                              <button
                                onClick={handleCopy(destination.walletAddress)}
                              >
                                <img
                                  src="/images/copy.png"
                                  alt="copy"
                                  className="size-4"
                                />
                              </button>
                            </>
                          ) : (
                            "-"
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex">
                    <span className="w-[200px] text-[#64748B] max-sm:text-sm">
                      Bank name
                    </span>
                    <span className="text-sm font-semibold text-text">
                      {destination?.bankName || "-"}
                    </span>
                  </div>
                  <div className="flex">
                    <span className="w-[200px] text-[#64748B] max-sm:text-sm">
                      Bank account no.
                    </span>
                    <span className="text-sm font-semibold text-text">
                      {destination?.bankAccountNumber || "-"}
                    </span>
                  </div>
                  <div className="flex">
                    <span className="w-[200px] text-[#64748B] max-sm:text-sm">
                      Bank account name
                    </span>
                    <span className="text-sm font-semibold text-text">
                      {destination?.bankAccountName || "-"}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="mt-8 space-y-2">
            <label className="block text-[16px] font-semibold text-[#1E293B]">
              {destination?.walletAddress ? "TXID" : "Transaction Reference"}
            </label>
            {txId ? (
              <div className="w-full text-wrap break-words rounded-lg border border-button-background bg-[#F6F9FB] px-4 py-3 text-left text-sm text-[#94A3B8]">
                {txId}
              </div>
            ) : (
              <input
                type="text"
                placeholder={destination?.walletAddress ? "Enter TXID" : "Enter transaction reference"}
                className={`w-full rounded-lg border bg-white ${
                  error ? "border-red-500" : "border-[#CBD5E1]"
                } px-4 py-3 text-sm placeholder:text-[#94A3B8]`}
                value={transactionId}
                onChange={(e) => {
                  setTransactionId(e.target.value);
                  setError("");
                }}
              />
            )}
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
          {!txId && (
            <div className="mt-8 rounded-[8px] border border-[#FDE047] bg-[#FEFCE8] px-4 py-[10px]">
              <p className="flex items-center gap-2 text-sm text-[#CA8A04]">
                <img src="/images/alert-triangle.png" alt="alert-triangle" />

                <span>
                  After you submit, this action is permanent and cannot be
                  undone.
                </span>
              </p>
            </div>
          )}

          {txId ? (
            <div className="mt-8 w-full rounded-lg">
              <button
                onClick={onClose}
                className="h-10 w-full rounded-lg bg-button-background text-[16px] font-medium text-[#94A3B8]"
              >
                Submitted
              </button>
            </div>
          ) : (
            <div className="mt-4 flex gap-4 max-sm:mt-2 max-sm:flex-col-reverse max-sm:gap-2">
              <button
                onClick={onClose}
                className="h-10 w-1/2 rounded-[6px] bg-button-background  text-[16px] font-medium text-text max-sm:w-full max-sm:rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!transactionId.trim()}
                className={`h-10 w-1/2 rounded-[6px] bg-text font-medium text-white disabled:opacity-50 max-sm:w-full max-sm:rounded-lg `}
              >
                Submit
              </button>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default CompleteRefundModal;
